import { createModelStore, type ModelStoreState } from '@/shared/stores/create-model-store'
import { parameterUtils, type HardwareItem, type ModelParameter } from '@/shared/stores/model-utils'
import { doorWindowModelInitialModel } from './initial-state'
import { doorWindowModelDimensionHandlers } from './dimensions'

export interface DoorWindowModelStoreState extends ModelStoreState {
  templates: any[]
  addHardware: (parentPositionId: string, hardwareType: string, hardware: HardwareItem) => void
  removeHardware: (
    parentPositionId: string,
    hardwareType: string,
    hardwarePositionId: string,
  ) => void
  setTemplates: (templates: any[]) => void
}

export const useDoorWindowModelStore = createModelStore({
  initialModel: doorWindowModelInitialModel,
  dimensionHandlers: doorWindowModelDimensionHandlers,
  extraActions: (set) => ({
    templates: [] as any[],

    addHardware: (parentPositionId: string, hardwareType: string, hardware: HardwareItem) =>
      set((s) => {
        if (!s.model.parameters) return

        const paramIndex = parameterUtils.findParameterIndex(s.model.parameters, parentPositionId)
        if (paramIndex === -1) return

        const param = s.model.parameters[paramIndex] as ModelParameter & {
          hardware?: Record<string, HardwareItem[] | undefined>
        }

        if (!param.hardware) {
          param.hardware = {}
        }

        if (!param.hardware[hardwareType]) {
          param.hardware[hardwareType] = []
        }

        const existingIndex = param.hardware[hardwareType]!.findIndex(
          (item) => item.positionId === hardware.positionId,
        )

        if (existingIndex !== -1) {
          param.hardware[hardwareType]![existingIndex] = {
            ...param.hardware[hardwareType]![existingIndex],
            ...hardware,
          }
        } else {
          param.hardware[hardwareType]!.push(hardware)
        }
      }),

    removeHardware: (parentPositionId: string, hardwareType: string, hardwarePositionId: string) =>
      set((s) => {
        if (!s.model.parameters) return

        const paramIndex = parameterUtils.findParameterIndex(s.model.parameters, parentPositionId)
        if (paramIndex === -1) return

        // invariant: paramIndex !== -1 guarded above
        const param = s.model.parameters[paramIndex]!
        if (!param.hardware?.[hardwareType]) return

        param.hardware[hardwareType] = param.hardware[hardwareType]!.filter(
          (item) => item.positionId !== hardwarePositionId,
        )
      }),

    setTemplates: (templates: any[]) =>
      set((s) => {
        s.templates = templates
      }),
  }),
}) as any as import('zustand').UseBoundStore<import('zustand').StoreApi<DoorWindowModelStoreState>>
